import React from 'react';
import CourseTooltip from './CourseTooltip';
import { Circle, Package, Users } from 'lucide-react';
import { formatDate, toHHMM } from '../../lib/dateFormat';
import {
  GROUP_COURSE_THRESHOLD,
  LARGE_GROUP_COURSE_THRESHOLD,
  formatParticipantPreview,
  getGroupSizeBucket,
  getParticipantCount,
  getParticipantNames,
} from '../../lib/groupCourses';

export interface ContingentInfo {
  remainingUnits: number;
  totalUnits: number;
}

export interface CourseBlockProps {
  course: any; // EnhancedCourse
  top: number;
  height: number;
  transparent?: boolean;
  left?: string;
  width?: string;
  onContextMenu?: (e: React.MouseEvent, course: any) => void;
  onClick?: (e: React.MouseEvent) => void;
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent<HTMLDivElement>, course: any) => void;
  onDragEnd?: () => void;
  contingentInfo?: ContingentInfo | null;
  getContingentColor?: (remainingUnits: number, totalUnits: number) => string;
  trials?: any[]; // TrialLessonDTO[] - Probestunden für diesen Kurs
  onContextMenuTrial?: (e: React.MouseEvent, trial: any) => void;
  onTrialDragStart?: (e: React.DragEvent<HTMLButtonElement>, trial: any) => void;
  onTrialDragEnd?: () => void;
  calcTop?: (time: string) => number;
  calcHeight?: (start: string, end: string) => number;
}

function getGroupBadge(participantCount: number): string | null {
  if (participantCount >= LARGE_GROUP_COURSE_THRESHOLD) return 'Großgruppe';
  if (participantCount >= GROUP_COURSE_THRESHOLD) return 'Gruppe';
  return null;
}

function getGroupMarkerClass(participantCount: number): string {
  const groupSizeBucket = getGroupSizeBucket(participantCount);
  if (groupSizeBucket === 'largeGroup') return 'border-l-4 border-l-violet-500';
  if (groupSizeBucket === 'group') return 'border-l-4 border-l-indigo-500';
  return '';
}

function renderTimeRow(course: any, isTiny: boolean, participantCount: number): React.ReactNode {
  if (isTiny) {
    return (
      <div className="flex items-center gap-1 truncate whitespace-nowrap text-[10px] font-medium leading-tight">
        <Circle className="h-2 w-2 fill-current" />
        <span>{toHHMM(course.startTime)}</span>
        {course.fachName ? <span className="truncate">| {course.fachName}</span> : null}
        {participantCount > 0 ? <span className="ml-1 inline-flex items-center gap-0.5">| <Users className="h-2.5 w-2.5" /> {participantCount}</span> : null}
      </div>
    );
  }

  return (
    <div className="font-medium truncate whitespace-nowrap text-[10px] leading-tight">
      {toHHMM(course.startTime)}-{toHHMM(course.endTime)}
      {course.fachName ? <span className="ml-1">| {course.fachName}</span> : null}
    </div>
  );
}

function TrialButtons({
  trials,
  height,
  onContextMenuTrial,
  onTrialDragStart,
  onTrialDragEnd,
}: Readonly<{
  trials: any[];
  height: number;
  onContextMenuTrial?: (e: React.MouseEvent, trial: any) => void;
  onTrialDragStart?: (e: React.DragEvent<HTMLButtonElement>, trial: any) => void;
  onTrialDragEnd?: () => void;
}>) {
  return (
    <div className="border-t mt-1 pt-1 space-y-0.5">
      {trials.map((trial) => {
        const dateLabel = trial.date ? formatDate(trial.date) : '';
        const fachName = trial.fachName || trial.instrument || 'Probestunde';
        const titleText = dateLabel
          ? `Probestunde: ${trial.studentName}\nDatum: ${dateLabel}`
          : `Probestunde: ${trial.studentName}`;
        return (
          <button
            type="button"
            key={trial.id}
            className="block w-full text-left text-[9px] leading-tight bg-yellow-200/60 border border-yellow-300/60 rounded px-1 py-0.5"
            draggable
            onClick={(e) => {
              e.stopPropagation();
              onContextMenuTrial?.(e, trial);
            }}
            onDragStart={(e) => {
              e.stopPropagation();
              onTrialDragStart?.(e, trial);
            }}
            onDragEnd={() => onTrialDragEnd?.()}
            onContextMenu={(e) => {
              e.stopPropagation();
              onContextMenuTrial?.(e, trial);
            }}
            title={titleText}
          >
            <div className="truncate whitespace-nowrap">{toHHMM(trial.startTime)} | {fachName}</div>
            {dateLabel && height >= 72 ? <div className="truncate text-[8px] opacity-75">{dateLabel}</div> : null}
            <div className="truncate text-[8px] opacity-75">{trial.studentName}</div>
          </button>
        );
      })}
    </div>
  );
}

const CourseBlock: React.FC<CourseBlockProps> = ({
  course,
  top,
  height,
  transparent,
  left,
  width,
  onContextMenu,
  onClick,
  draggable,
  onDragStart,
  onDragEnd,
  contingentInfo,
  getContingentColor,
  trials = [],
  onContextMenuTrial,
  onTrialDragStart,
  onTrialDragEnd,
  calcTop,
  calcHeight
}) => {
  const hasColumnLayout = !!left && !!width;
  const isNarrow = Number(course?.columns ?? 1) > 1;
  const isTiny = height < 26;
  const isCompact = height < 44 || isNarrow;
  const isPlaceholder = Boolean(course?.isPlaceholder);
  const showTrialsInCard = !isCompact && trials.length > 0;
  const showContingent = !isTiny && !!contingentInfo && !!getContingentColor;
  const participantNames = getParticipantNames(course);
  const participantCount = getParticipantCount(course);
  const previewLimit = !isCompact && height >= 78 && !isNarrow ? 3 : 2;
  const { preview: participantPreview, hiddenCount } = formatParticipantPreview(participantNames, previewLimit);
  const showParticipantPreview = participantCount > 0 && !isTiny && !isCompact;
  const groupBadge = getGroupBadge(participantCount);
  const groupMarkerClass = getGroupMarkerClass(participantCount);

  return (
    <CourseTooltip
      course={course}
      className={`absolute ${hasColumnLayout ? '' : 'inset-x-1'} overflow-hidden rounded-md border p-1 text-xs ${course.color} ${groupMarkerClass} ${isPlaceholder ? 'border-dashed border-amber-500/80 bg-amber-50/95 text-amber-900' : ''} cursor-pointer z-10 hover:shadow-md transition-all`}
      style={{ top: `${top}px`, height: `${height}px`, opacity: transparent ? 0.5 : 1, ...(hasColumnLayout ? { left, width } : {}) }}
      onContextMenu={(e) => onContextMenu?.(e, course)}
      onClick={(e) => onClick?.(e)}
      draggable={draggable}
      onDragStart={(e) => onDragStart?.(e, course)}
      onDragEnd={onDragEnd}
      onShowDetails={onClick}
    >
      {isPlaceholder && !isTiny ? (
        <div className="mb-0.5 inline-flex rounded bg-amber-200/80 px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide">
          Platzhalter
        </div>
      ) : null}
      {course?.hint && !isTiny ? (
        <div className="mb-0.5 inline-flex rounded bg-violet-200/80 px-1 py-0.5 text-[9px] font-semibold tracking-wide text-violet-900">
          {course.hint}
        </div>
      ) : null}
      {renderTimeRow(course, isTiny, participantCount)}
      {participantCount > 0 && (
        <div className="absolute right-1 top-1 z-10">
          <span className="inline-flex items-center gap-1 rounded bg-black/10 px-1.5 py-0.5 text-[10px] font-semibold leading-tight">
            <Users className="h-2.5 w-2.5" />
            {participantCount}
            {groupBadge ? <span className="hidden sm:inline">• {groupBadge}</span> : null}
          </span>
        </div>
      )}
      {showParticipantPreview && (
        <div className="truncate whitespace-nowrap text-[10px] leading-tight mt-0.5 border-t pt-0.5">
          {participantPreview}
          {hiddenCount > 0 ? ` +${hiddenCount}` : ''}
        </div>
      )}
      {showTrialsInCard ? (
        <TrialButtons
          trials={trials}
          height={height}
          onContextMenuTrial={onContextMenuTrial}
          onTrialDragStart={onTrialDragStart}
          onTrialDragEnd={onTrialDragEnd}
        />
      ) : null}
      {showContingent && (
        <div className={`flex items-center font-medium text-[10px] mt-0.5 ${getContingentColor(contingentInfo.remainingUnits, contingentInfo.totalUnits)}`}>
          <Package className="h-2.5 w-2.5 mr-1" />
          <span>{contingentInfo.remainingUnits}/{contingentInfo.totalUnits}</span>
        </div>
      )}
    </CourseTooltip>
  );
};

export default CourseBlock;


